import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { Map, HelpCircle, GitCompare, Radio } from "lucide-react";
import { motion } from "framer-motion";
import ShareApp from "@/components/ShareApp";
import UserMenu from "@/components/UserMenu";

const LOGO_SPIN = { initial: { rotate: -8 }, animate: { rotate: 0 }, transition: { type: "spring", stiffness: 180 } };

export default function Header() {
  const { lang, setLang, t } = useLang();
  const location = useLocation();

  const nav = [
    { to: "/", label: t("map"), icon: Map, testid: "nav-map" },
    { to: "/quiz", label: t("quiz"), icon: HelpCircle, testid: "nav-quiz" },
    { to: "/compare", label: t("compare"), icon: GitCompare, testid: "nav-compare" },
    { to: "/radio", label: t("radio"), icon: Radio, testid: "nav-radio" },
  ];

  return (
    <header className="site-header sticky top-0 z-40 border-b border-stoneLine/70 bg-sand/90 backdrop-blur-xl" data-testid="site-header">
      <div className="mx-auto max-w-7xl px-3 sm:px-8 py-2 sm:py-4 flex flex-wrap items-center gap-x-6 gap-y-1.5">
        <Link to="/" className="flex items-center gap-2.5 sm:gap-3 group min-w-0" data-testid="brand-link">
          <motion.img
            {...LOGO_SPIN}
            src="/logo-mark.svg"
            alt=""
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl shadow-sm shrink-0"
            data-testid="brand-logo"
          />
          <div className="leading-tight min-w-0">
            <div className="text-base sm:text-xl font-display font-bold text-ink truncate" data-testid="app-title">
              {t("app_title")}
            </div>
            <div className="text-xs text-muted-foreground hidden sm:block">{t("app_tagline")}</div>
          </div>
        </Link>

        <div className="order-last ms-2 flex items-center gap-1.5 shrink-0">
          <ShareApp />
          <UserMenu />
        </div>

        <nav className="ms-auto md:w-auto flex items-center justify-end md:justify-start gap-x-0.5 sm:gap-2">
          {nav.map((n) => {
            const active = location.pathname === n.to;
            const Icon = n.icon;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                title={n.label}
                aria-label={n.label}
                className={`hidden md:inline-flex px-4 py-2 rounded-full text-sm flex-row items-center gap-2 transition-colors duration-200 border ${
                  active
                    ? "bg-ink text-sand border-ink"
                    : "border-transparent text-ink/80 hover:text-ink hover:bg-sandDeep/50"
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-[10px] leading-none sm:text-sm sm:leading-normal">{n.label}</span>
              </Link>
            );
          })}

          <div className="ms-1 sm:ms-2 flex items-center rounded-full border border-stoneLine bg-card p-0.5 shrink-0" data-testid="lang-switch">
            {["ar", "en", "it", "fr"].map((code) => (
              <button
                key={code}
                onClick={() => setLang(code)}
                data-testid={`lang-${code}`}
                className={`px-1.5 sm:px-3 py-1 text-[11px] sm:text-xs uppercase tracking-wider rounded-full transition-colors ${
                  lang === code ? "bg-ochre text-white" : "text-ink/70 hover:text-ink"
                }`}
              >
                {code}
              </button>
            ))}
          </div>
        </nav>
      </div>
    </header>
  );
}
